import React from 'react';
import { motion } from 'framer-motion';
import { Printer, Mail, Download, CheckCircle } from 'lucide-react';

function formatDate(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' });
}

export default function Receipt({
  transaction,
  onPrint,
  onEmail,
  onDownload,
  onClose,
  shopName = 'Devoard POS',
  shopAddress = '123 Main Street, Lahore',
  shopPhone = '+92 300 1234567',
}) {
  if (!transaction) return null;

  const {
    id,
    date,
    items = [],
    subtotal = 0,
    discount = 0,
    tax = 0,
    total = 0,
    paymentMethod = 'Cash',
    amountTendered = 0,
    change = 0,
    operatorName = '',
  } = transaction;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden max-w-sm mx-auto"
    >
      <div className="p-6">
        <div className="text-center mb-5">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{shopName}</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">{shopAddress}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{shopPhone}</p>
        </div>

        <div className="text-center mb-5">
          <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-2">
            <CheckCircle size={24} className="text-emerald-600 dark:text-emerald-400" />
          </div>
          <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">Payment Successful</p>
        </div>

        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-4 pb-4 border-b border-dashed border-gray-200 dark:border-gray-700">
          <div>
            <p>Transaction ID</p>
            <p className="font-mono text-gray-900 dark:text-white">{id || 'N/A'}</p>
          </div>
          <div className="text-right">
            <p>{formatDate(date)}</p>
            <p className="text-gray-900 dark:text-white">{formatTime(date)}</p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-xs font-medium text-gray-500 dark:text-gray-400 pb-1 border-b border-gray-200 dark:border-gray-700">
            <span className="flex-1">Item</span>
            <span className="w-16 text-center">Qty</span>
            <span className="w-20 text-right">Price</span>
            <span className="w-24 text-right">Total</span>
          </div>
          {items.map((item, idx) => (
            <div key={idx} className="flex justify-between text-sm">
              <span className="flex-1 text-gray-900 dark:text-white truncate">{item.name}</span>
              <span className="w-16 text-center text-gray-500 dark:text-gray-400">x{item.quantity || 1}</span>
              <span className="w-20 text-right text-gray-500 dark:text-gray-400">Rs. {(item.salePrice || item.price || 0).toLocaleString()}</span>
              <span className="w-24 text-right font-medium text-gray-900 dark:text-white">
                Rs. {((item.salePrice || item.price || 0) * (item.quantity || 1)).toLocaleString()}
              </span>
            </div>
          ))}
        </div>

        <div className="space-y-1.5 pt-4 border-t border-dashed border-gray-200 dark:border-gray-700">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">Subtotal</span>
            <span className="text-gray-900 dark:text-white">Rs. {subtotal.toLocaleString()}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-red-500 dark:text-red-400">Discount</span>
              <span className="text-red-500 dark:text-red-400">-Rs. {discount.toLocaleString()}</span>
            </div>
          )}
          {tax > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">Tax</span>
              <span className="text-gray-900 dark:text-white">Rs. {tax.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200 dark:border-gray-700">
            <span className="text-gray-900 dark:text-white">Grand Total</span>
            <span className="text-primary-600 dark:text-primary-400">Rs. {total.toLocaleString()}</span>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-dashed border-gray-200 dark:border-gray-700 space-y-1 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex justify-between">
            <span>Payment Method</span>
            <span className="font-medium text-gray-900 dark:text-white capitalize">{paymentMethod}</span>
          </div>
          {paymentMethod === 'cash' && change > 0 && (
            <>
              <div className="flex justify-between">
                <span>Amount Tendered</span>
                <span className="text-gray-900 dark:text-white">Rs. {amountTendered.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Change</span>
                <span className="text-emerald-600 dark:text-emerald-400">Rs. {change.toLocaleString()}</span>
              </div>
            </>
          )}
          {operatorName && (
            <div className="flex justify-between">
              <span>Operator</span>
              <span className="text-gray-900 dark:text-white">{operatorName}</span>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-5 pt-4 border-t border-dashed border-gray-200 dark:border-gray-700">
          Thank you for your purchase!
        </p>
      </div>

      <div className="flex border-t border-gray-200 dark:border-gray-700 divide-x divide-gray-200 dark:divide-gray-700">
        <button
          onClick={onPrint}
          className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <Printer size={16} />
          Print
        </button>
        <button
          onClick={onEmail}
          className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <Mail size={16} />
          Email
        </button>
        <button
          onClick={onDownload}
          className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <Download size={16} />
          Save
        </button>
      </div>
    </motion.div>
  );
}
